import { useSyncExternalStore } from 'react'

import {
  DEFAULT_READ_ALOUD_SPEED,
  DEFAULT_READ_ALOUD_VOICE,
  normalizeReadAloudSpeed,
  type ReadAloudVoice,
} from './readAloudSettings'
import { splitReadAloudText } from './readAloudText'

export type ReadAloudSource = 'manual' | 'auto'
export type ReadAloudStatus = 'idle' | 'loading' | 'playing' | 'error'

export type ReadAloudSnapshot = {
  status: ReadAloudStatus
  messageId: string | null
  source: ReadAloudSource | null
  error: string | null
}

export type SpeakMessageAloudInput = {
  messageId: string
  body: string
  source: ReadAloudSource
  voice?: ReadAloudVoice
  speed?: number
}

let snapshot: ReadAloudSnapshot = {
  status: 'idle',
  messageId: null,
  source: null,
  error: null,
}
const listeners = new Set<() => void>()
let activeRun = 0
let activeAbort: AbortController | null = null
let activeAudio: HTMLAudioElement | null = null

function emit(next: ReadAloudSnapshot): void {
  snapshot = next
  for (const listener of listeners) listener()
}

function cleanupAudio(): void {
  if (activeAudio) {
    activeAudio.pause()
    activeAudio.removeAttribute('src')
    activeAudio.load()
    activeAudio = null
  }
}

function abortActivePlayback(): void {
  activeAbort?.abort()
  activeAbort = null
  cleanupAudio()
}

export function subscribeReadAloud(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getReadAloudSnapshot(): ReadAloudSnapshot {
  return snapshot
}

export function useReadAloudState(): ReadAloudSnapshot {
  return useSyncExternalStore(subscribeReadAloud, getReadAloudSnapshot, getReadAloudSnapshot)
}

export function stopReadAloud(): void {
  activeRun += 1
  abortActivePlayback()
  emit({ status: 'idle', messageId: null, source: null, error: null })
}

type ReadAloudSessionResponse = {
  ok: boolean
  stream_url: string
  cache: 'HIT' | 'MISS'
  response_format: string
  mime_type: string
}

async function createReadAloudStream({
  text,
  messageId,
  voice,
  speed,
  signal,
}: {
  text: string
  messageId: string
  voice: ReadAloudVoice
  speed: number
  signal: AbortSignal
}): Promise<ReadAloudSessionResponse> {
  const response = await fetch('/api/voice/read-aloud/session', {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message_id: messageId,
      text,
      voice,
      speed,
      response_format: 'mp3',
    }),
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new Error(detail ? detail.slice(0, 240) : `read-aloud failed (${response.status})`)
  }
  const payload = await response.json() as Partial<ReadAloudSessionResponse>
  if (!payload.stream_url || payload.ok !== true) {
    throw new Error('read-aloud stream was not created')
  }
  return payload as ReadAloudSessionResponse
}

function sameOriginAudioUrl(streamUrl: string): string {
  return new URL(streamUrl, window.location.origin).toString()
}

function playStreamUrl(streamUrl: string, signal: AbortSignal, onPlaying: () => void): Promise<void> {
  return new Promise((resolve, reject) => {
    cleanupAudio()
    const audio = new Audio()
    activeAudio = audio
    audio.preload = 'auto'
    audio.src = sameOriginAudioUrl(streamUrl)
    let didStart = false

    function cleanupListeners() {
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('error', onError)
      audio.removeEventListener('playing', onPlayingEvent)
      signal.removeEventListener('abort', onAbort)
    }
    function onPlayingEvent() {
      if (didStart) return
      didStart = true
      onPlaying()
    }
    function onEnded() {
      cleanupListeners()
      cleanupAudio()
      resolve()
    }
    function onError() {
      cleanupListeners()
      cleanupAudio()
      reject(new Error('read-aloud playback failed'))
    }
    function onAbort() {
      cleanupListeners()
      cleanupAudio()
      resolve()
    }

    audio.addEventListener('ended', onEnded)
    audio.addEventListener('error', onError)
    audio.addEventListener('playing', onPlayingEvent)
    signal.addEventListener('abort', onAbort, { once: true })
    audio.play().catch((error: unknown) => {
      cleanupListeners()
      cleanupAudio()
      reject(error instanceof Error ? error : new Error('read-aloud playback was blocked'))
    })
  })
}

export async function speakMessageAloud({
  messageId,
  body,
  source,
  voice = DEFAULT_READ_ALOUD_VOICE,
  speed = DEFAULT_READ_ALOUD_SPEED,
}: SpeakMessageAloudInput): Promise<boolean> {
  const chunks = splitReadAloudText(body)
  const playbackSpeed = normalizeReadAloudSpeed(speed)
  if (chunks.length === 0) {
    emit({
      status: 'error',
      messageId,
      source,
      error: 'Nothing readable in this message.',
    })
    return false
  }

  activeRun += 1
  const run = activeRun
  abortActivePlayback()
  const abort = new AbortController()
  activeAbort = abort
  emit({ status: 'loading', messageId, source, error: null })

  try {
    for (const chunk of chunks) {
      if (run !== activeRun || abort.signal.aborted) return false
      const stream = await createReadAloudStream({
        text: chunk,
        messageId,
        voice,
        speed: playbackSpeed,
        signal: abort.signal,
      })
      if (run !== activeRun || abort.signal.aborted) return false
      await playStreamUrl(stream.stream_url, abort.signal, () => {
        if (run === activeRun && !abort.signal.aborted) {
          emit({ status: 'playing', messageId, source, error: null })
        }
      })
    }
    if (run === activeRun) {
      activeAbort = null
      emit({ status: 'idle', messageId: null, source: null, error: null })
    }
    return true
  } catch (error) {
    if (run !== activeRun || abort.signal.aborted) return false
    activeAbort = null
    cleanupAudio()
    emit({
      status: 'error',
      messageId,
      source,
      error: error instanceof Error ? error.message : 'Read-aloud failed.',
    })
    return false
  }
}
